/**
 * Global JavaScript utilities and cart functionality
 */

// Debounce utility
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Fetch config for Shopify AJAX API
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

// Cart class for managing cart operations
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.js-cart-drawer');
    this.body = document.querySelector('.cart-drawer__body');
    this.countElements = document.querySelectorAll('.js-cart-count');
    this.isUpdating = false; // Lock to prevent concurrent updates
    this.pendingUpdate = null; // Store pending update if one is in progress
    this.autoCloseTimer = null;
    this.isHovering = false;
    this.isInteracting = false;

    this.bindEvents();
  }

  bindEvents() {
    // Close cart - use event delegation on document for dynamically added content
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.js-cart-close');
      if (closeBtn) {
        e.preventDefault();
        this.close();
      }
    });

    // Close on overlay click
    document.addEventListener('click', (e) => {
      if (e.target.closest('.cart-drawer__overlay')) {
        this.close();
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
        this.close();
      }
    });

    // Quantity + remove are handled EXCLUSIVELY by the inline script in
    // sections/cart-drawer.liquid. Binding them here too made every click fire
    // twice — the second removal, computed against the pre-removal line indexes,
    // deleted a shifted line as well, so removing one item wiped the whole cart
    // (and +/- jumped by two). The inline handler also owns refreshCart(), which
    // re-inits reviews / shipping calc / samples, so it must be the sole owner.
    // Intentionally NOT bound here. See cart-drawer.liquid handleDrawerClick().

    // Desktop: pause auto-close on hover over the drawer panel
    var panel = this.drawer?.querySelector('.cart-drawer__panel') || this.drawer;
    if (panel) {
      panel.addEventListener('mouseenter', () => {
        this.isHovering = true;
        this.clearAutoClose();
      });
      panel.addEventListener('mouseleave', () => {
        this.isHovering = false;
        if (this.drawer?.classList.contains('is-open')) {
          this.startAutoClose();
        }
      });
    }

    // Mobile: any touch/scroll inside drawer resets the timer
    if (this.drawer) {
      this.drawer.addEventListener('touchstart', () => {
        this.resetAutoClose();
      }, { passive: true });
      this.drawer.addEventListener('scroll', () => {
        this.resetAutoClose();
      }, { passive: true, capture: true });
    }
  }

  startAutoClose() {
    // Auto-close disabled. The drawer was closing itself ~2s after opening, which
    // read as "the cart closes too fast." Carts now stay open until the shopper
    // dismisses them (overlay tap / X / Escape / swipe). clearAutoClose() and
    // resetAutoClose() remain no-op-safe for the hover handlers that call them.
    this.clearAutoClose();
  }

  clearAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  resetAutoClose() {
    if (this.drawer?.classList.contains('is-open')) {
      this.startAutoClose();
    }
  }

  open() {
    this.drawer?.classList.add('is-open');
    document.body.classList.add('no-scroll');
    this.startAutoClose();
  }

  close() {
    this.clearAutoClose();
    this.drawer?.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  }

  async addItem(variantId, quantity = 1, sellingPlan = null) {
    try {
      // Open drawer immediately for instant feedback
      this.open();

      // Use JSON for cart/add endpoint for more reliable response.
      // keepalive: true lets the add finish committing even if the shopper
      // immediately clicks the Cart link and navigates away (otherwise the
      // browser cancels the in-flight request and the item is lost).
      const addBody = {
        id: parseInt(variantId),
        quantity: quantity
      };
      // Attach the subscription selling plan when one was chosen — this is what
      // makes Shopify bill the order on a recurring schedule.
      if (sellingPlan) {
        addBody.selling_plan = parseInt(sellingPlan);
      }
      const response = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(addBody)
      });

      const data = await response.json();

      if (response.ok) {
        // Short delay then refresh content (drawer is already open and sliding in)
        await new Promise(resolve => setTimeout(resolve, 100));
        await this.refresh();
      } else {
        console.error('Error adding to cart:', data);
        alert(data.description || 'Error adding to cart');
        this.close();
      }

      return data;
    } catch (error) {
      console.error('Error in addItem:', error);
      this.close();
    }
  }

  async updateQuantity(key, change) {
    // Prevent concurrent updates - if already updating, queue this request
    if (this.isUpdating) {
      // Store the latest pending update (overwrites any previous pending)
      this.pendingUpdate = { key, change };
      return;
    }

    this.isUpdating = true;

    // Disable quantity buttons during update
    const buttons = document.querySelectorAll('.js-quantity-minus, .js-quantity-plus');
    buttons.forEach(btn => btn.disabled = true);

    // Re-query the drawer in case it was refreshed
    const drawer = document.querySelector('.js-cart-drawer');
    const input = drawer?.querySelector(`input[data-key="${key}"]`);
    if (!input) {
      console.error('Could not find input for key:', key);
      this.isUpdating = false;
      buttons.forEach(btn => btn.disabled = false);
      return;
    }

    const currentQty = parseInt(input.value) || 1;
    const newQuantity = Math.max(1, currentQty + change); // Min 1, use remove button for 0

    // Update input immediately for visual feedback
    input.value = newQuantity;

    try {
      // Get current cart to find line index
      const cartResponse = await fetch(window.routes.cart_url + '.js');
      const cartData = await cartResponse.json();
      const lineIndex = cartData.items.findIndex(item => item.key === key) + 1;

      if (lineIndex === 0) {
        console.error('Item not found in cart:', key);
        await this.refresh();
        return;
      }

      const response = await fetch(window.routes.cart_change_url + '.js', {
        ...fetchConfig(),
        body: JSON.stringify({
          line: lineIndex,
          quantity: newQuantity
        })
      });

      if (response.ok) {
        await this.refresh();
      } else {
        const errorData = await response.json();
        console.error('Failed to update quantity:', errorData);
        // Show user-friendly error message
        if (errorData.description) {
          alert(errorData.description);
        }
        // Refresh to show correct state
        await this.refresh();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      await this.refresh();
    } finally {
      this.isUpdating = false;

      // Re-enable buttons
      const newButtons = document.querySelectorAll('.js-quantity-minus, .js-quantity-plus');
      newButtons.forEach(btn => btn.disabled = false);

      // Process any pending update
      if (this.pendingUpdate) {
        const pending = this.pendingUpdate;
        this.pendingUpdate = null;
        this.updateQuantity(pending.key, pending.change);
      }
    }
  }

  async removeItem(key) {

    try {
      // First get current cart to find the line index
      const cartResponse = await fetch(window.routes.cart_url + '.js');
      const cartData = await cartResponse.json();

      // Find the line index (1-based) for this item key
      const lineIndex = cartData.items.findIndex(item => item.key === key) + 1;

      if (lineIndex === 0) {
        console.error('Item not found in cart:', key);
        await this.refresh();
        return;
      }

      const response = await fetch(window.routes.cart_change_url + '.js', {
        ...fetchConfig(),
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0
        })
      });

      const result = await response.json();

      if (response.ok) {
        await this.refresh();
      }
    } catch (error) {
      console.error('Error removing item:', error);
      await this.refresh();
    }
  }

  async refresh() {
    try {
      // First, get the actual cart data to verify items exist
      const cartData = await fetch(window.routes.cart_url + '.js', {
        cache: 'no-store'
      }).then(r => r.json());


      // Add timestamp and random to prevent any caching
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(7);
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer&t=${timestamp}&r=${random}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update cart drawer content - re-query to get current drawer element
      const currentDrawer = document.querySelector('.js-cart-drawer');
      const newDrawer = doc.querySelector('.js-cart-drawer') || doc.querySelector('.cart-drawer');

      // Debug logging

      // Check if new drawer has items
      const newItems = newDrawer?.querySelectorAll('.cart-item');

      if (newDrawer && currentDrawer) {
        // Preserve the is-open class if drawer was open
        const wasOpen = currentDrawer.classList.contains('is-open');
        currentDrawer.innerHTML = newDrawer.innerHTML;
        if (wasOpen) {
          currentDrawer.classList.add('is-open');
        }
        this.drawer = currentDrawer;
      } else {
        console.error('Cart refresh failed - drawer element not found. Check section_id matches filename.');
        console.log('Response HTML preview:', html.substring(0, 500));
      }

      // Update cart count everywhere on page
      document.querySelectorAll('.js-cart-count').forEach(el => {
        el.textContent = cartData.item_count;
      });

      // Keep the header cart link's aria-label in sync (server-rendered once,
      // otherwise it stays "Cart (0 items)" after AJAX adds)
      const _n = cartData.item_count;
      document.querySelectorAll('.site-header__cart-btn').forEach(el => {
        el.setAttribute('aria-label', 'Cart (' + _n + ' ' + (_n === 1 ? 'item' : 'items') + ')');
      });

    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}

// Initialize cart drawer after DOM is ready
let cart = null;

function initCart() {
  console.log('initCart called');
  cart = new CartDrawer();
  window.cart = cart;

  // Handle ALL form submissions for add to cart
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    const action = form.getAttribute('action') || '';
    const formId = form.id || '';
    const hasVariantInput = !!form.querySelector('[name="id"]');

    // Check if this is an add to cart form - multiple ways to detect
    const isCartForm = action.includes('/cart/add') ||
                       (typeof formId === 'string' && formId.includes('product-form')) ||
                       formId === 'addToCartForm' ||
                       (hasVariantInput && form.classList.contains('product-form')) ||
                       (hasVariantInput && form.classList.contains('add-to-cart-form'));

    if (!isCartForm) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const submitButton = form.querySelector('[type="submit"], button[name="add"], .btn--primary');
    if (!submitButton) {
      console.error('No submit button found');
      return;
    }

    const originalHTML = submitButton.innerHTML;
    submitButton.disabled = true;
    submitButton.textContent = 'Adding...';

    // Resolve the variant the way the browser would on a real submit. The old
    // form.querySelector('[name="id"]') returned the FIRST element named "id",
    // which for a radio group is the first radio rather than the checked one —
    // on the sample product (12 scent radios) that is always "People Watching",
    // so every sample went into the cart as that scent no matter which one the
    // customer picked. FormData handles radios, <select> and hidden inputs.
    const addFormData = new FormData(form);
    let variantId = addFormData.get('id');
    if (variantId === null || variantId === '') {
      // no radio checked / field disabled — fall back to a lone id field
      const variantInput = form.querySelector('[name="id"]');
      variantId = variantInput ? variantInput.value : null;
    }
    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    // Subscription: a non-empty selling_plan turns this into a recurring order.
    const sellingPlanInput = form.querySelector('[name="selling_plan"]');
    const sellingPlan = sellingPlanInput && sellingPlanInput.value ? sellingPlanInput.value : null;

    if (variantId) {
      await cart.addItem(variantId, quantity, sellingPlan);
    } else {
      console.error('No variant ID found in form');
    }

    submitButton.disabled = false;
    submitButton.innerHTML = originalHTML;
  }, true); // Use capture phase to intercept before default behavior
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCart);
} else {
  initCart();
}
